import { zodResolver } from "@hookform/resolvers/zod";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { TabContext, TabPanel } from "@mui/lab";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, Grid, Tab, Tabs, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";
import { CustomerSelectionFormControl } from "../../components/form/CustomerSelectionFormControl/CustomerSelectionFormControl";
import { DatePeriodSelector } from "../../components/form/DatePeriodSelector";
import { DiscountFormControl } from "../../components/form/DiscountFormControl";
import { ProductSelectionFormControl } from "../../components/form/ProductSelectionFormControl/ProductSelectionFormControl";
import { STATUS_KEYS } from "../../schemas/status";
import { useUpdateBookingMutation } from "../../services/bookingsService";
import { useGetProductsBookedDays, useProducts } from "../../services/productsService";
import { normalizePercentageForSubmission } from "../../utils";
import { editBookingFormDataSchema } from "./EditBookingFormData";

export const BookingEditDialog = (props) => {
    const { initialBooking, onSuccess, open, onClose } = props;
    const { t } = useTranslation();
    const [tab, setTab] = useState("clients");
    const form = useForm({
        resolver: zodResolver(editBookingFormDataSchema),
        defaultValues: {
            customerIds: initialBooking.customers.map((c) => c.customer.id),
            productIds: initialBooking.products.map((p) => p.id),
            discounts: initialBooking.offer
                ? Object.fromEntries(
                      initialBooking.offer.products
                          .filter((op) =>
                              initialBooking.products.some((p) => p.id === op.product.id),
                          )
                          .map((op) => [
                              op.product.id,
                              Number(Number(op.discount_percent).toFixed(2)),
                          ]),
                  )
                : {},
            startDate: new Date(initialBooking.start),
            endDate: new Date(initialBooking.end),
            note: initialBooking.note,
            offerId: initialBooking.offer?.id,
        },
    });

    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        formState: { errors },
    } = form;

    const { data: allProductsData } = useProducts();
    const allProducts = allProductsData?.results || [];

    const { mutate: updateBooking, isPending } = useUpdateBookingMutation({
        onSuccess: () => {
            toast.custom(
                (curToast) => <SnackbarSuccess message={t("bookings.editBookingDialog.success")} />,
                {
                    position: "bottom-right",
                },
            );
            onSuccess?.();
            onClose();
        },
        onError: (error) => {
            toast.custom(
                (curToast) => <SnackbarError message={t("bookings.editBookingDialog.error")} />,
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const productIds = useWatch({ control, name: "productIds" });
    const selectedProducts = allProducts.filter((p) => productIds.includes(p.id));

    const bookingDurationLimits = selectedProducts.map(
        (p) => p.investment.invest_booking_duration_days,
    );
    const maxDuration =
        bookingDurationLimits.length > 0 ? Math.min(...bookingDurationLimits) : undefined;

    const onSubmit = (data) => {
        const requestBody = {
            customer_ids: data.customerIds,
            note: data.note,
            products: Object.entries(data.discounts).map(([productId, discountPercent]) => ({
                product_id: productId,
                discount_percent: normalizePercentageForSubmission(discountPercent),
            })),
            offer_id: data.offerId,
        };
        updateBooking({ bookingId: initialBooking.id, data: requestBody });
    };

    const discounts = useWatch({ control, name: "discounts" });

    useEffect(() => {
        const newDiscounts = { ...(discounts || {}) };
        let changed = false;

        // Add entries for new products
        for (const id of productIds) {
            if (!(id in newDiscounts)) {
                newDiscounts[id] = 0; // Default discount for new product
                changed = true;
            }
        }

        // Remove entries for deselected products
        for (const id in newDiscounts) {
            if (!productIds.includes(id)) {
                delete newDiscounts[id];
                changed = true;
            }
        }

        if (changed) {
            setValue("discounts", newDiscounts);
        }
    }, [productIds, discounts, setValue]);

    const { data: excludedDates = [] } = useGetProductsBookedDays(productIds, null, {
        select: (data) =>
            data.booked_days.map(
                (day) =>
                    new Date(
                        new Date(day).getFullYear(),
                        new Date(day).getMonth(),
                        new Date(day).getDate(),
                    ),
            ),
        enabled: productIds.length > 0,
    });

    const customerIds = useWatch({ control, name: "customerIds" });
    const startDate = useWatch({ control, name: "startDate" });
    const endDate = useWatch({ control, name: "endDate" });
    const note = useWatch({ control, name: "note" });

    const handleNext = () => {
        if (tab === "clients") {
            setTab("products");
        } else if (tab === "products") {
            setTab("discounts");
        } else if (tab === "discounts") {
            setTab("booking");
        }
    };

    const handleBack = () => {
        if (tab === "booking") {
            setTab("discounts");
        } else if (tab === "discounts") {
            setTab("products");
        } else if (tab === "products") {
            setTab("clients");
        }
    };

    const isNextDisabled = () => {
        if (tab === "clients" && customerIds.length === 0) {
            return true;
        }
        if (tab === "products") {
            if (productIds.length === 0) {
                return true;
            }

            if (allProducts.length > 0) {
                const hasApartment = selectedProducts.some((p) => p.is_apartment);
                const hasOrphan = selectedProducts.some(
                    (p) => !p.is_apartment && p.parent_product == null,
                );

                if (hasOrphan && !hasApartment) {
                    return true;
                }
            }
        }
        if (tab === "discounts") {
            return false;
        }
        if (tab === "booking") {
            return true;
        }
        return false;
    };

    const onStartDateChange = useCallback(
        (date) => {
            if (date) {
                setValue("startDate", date, { shouldValidate: true });
            }
        },
        [setValue],
    );

    const onEndDateChange = useCallback(
        (date) => {
            if (date) {
                setValue("endDate", date, { shouldValidate: true });
            }
        },
        [setValue],
    );

    return (
        <BasicDialog
            open={open}
            onClose={onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("bookings.editBookingDialog.title")}
            showDialogActions
            alwaysFullScreen={true}
            contentSx={{ pt: 0 }}
            renderFooter={() => (
                <Grid container justifyContent={"space-between"} gap={2} sx={{ pb: 2, px: 2 }}>
                    <Grid item xs>
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<KeyboardArrowLeftIcon />}
                            disabled={tab === "clients"}
                            onClick={handleBack}
                        >
                            {t("bookings.addBookingDialog.back")}
                        </Button>
                    </Grid>
                    <Grid item xs>
                        {tab === "booking" ? (
                            <LoadingButton
                                variant="contained"
                                fullWidth
                                color="success"
                                onClick={handleSubmit(onSubmit)}
                                disabled={isPending}
                                loading={isPending}
                            >
                                {t("bookings.addBookingDialog.save")}
                            </LoadingButton>
                        ) : (
                            <Button
                                variant="contained"
                                fullWidth
                                endIcon={<KeyboardArrowRightIcon />}
                                disabled={isNextDisabled()}
                                onClick={handleNext}
                            >
                                {t("bookings.addBookingDialog.next")}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            )}
        >
            <FormProvider {...form}>
                <TabContext value={tab}>
                    <Tabs
                        variant="fullWidth"
                        value={tab}
                        onChange={(e, newValue) => setTab(newValue)}
                    >
                        <Tab
                            value={"clients"}
                            label={t("bookings.addBookingDialog.chooseClient")}
                        />
                        <Tab
                            disabled={customerIds.length === 0}
                            value={"products"}
                            label={t("bookings.addBookingDialog.chooseProducts")}
                        />
                        <Tab
                            disabled={productIds.length === 0}
                            value="discounts"
                            label={t("bookings.addBookingDialog.discounts")}
                        />
                        <Tab
                            disabled={productIds.length === 0}
                            value="booking"
                            label={t("bookings.addBookingDialog.chooseDates")}
                        />
                    </Tabs>
                    <TabPanel value="clients" sx={{ p: 0, pt: 2 }}>
                        <CustomerSelectionFormControl control={control} fieldName="customerIds" />
                    </TabPanel>
                    <TabPanel value="products" sx={{ p: 0, pt: 2 }}>
                        <ProductSelectionFormControl
                            multipleApartments={false}
                            selectedProductIds={productIds}
                            setSelectedProductIds={(ids) =>
                                setValue("productIds", ids, { shouldValidate: true })
                            }
                            disableApartmentSelection={true}
                            excludedStatuses={[
                                STATUS_KEYS.Sold,
                                STATUS_KEYS.Reservation,
                                STATUS_KEYS.FictionalReservation,
                            ]}
                            readOnly={true}
                        />
                    </TabPanel>
                    <TabPanel value="discounts" sx={{ p: 0, pt: 2 }}>
                        <DiscountFormControl fields={{ discount: "discounts" }} />
                    </TabPanel>
                    <TabPanel value="booking" sx={{ p: 0, pt: 2 }}>
                        <Grid container spacing={2} direction="column" alignItems="center">
                            <Grid item sx={{ width: "75%" }}>
                                <DatePeriodSelector
                                    startDate={startDate}
                                    endDate={endDate}
                                    excludedDates={excludedDates}
                                    onStartDateChange={onStartDateChange}
                                    onEndDateChange={onEndDateChange}
                                    maxDuration={maxDuration}
                                    readOnly={true}
                                />
                            </Grid>
                            <Grid item sx={{ width: "75%" }}>
                                <Controller
                                    name="note"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`${t("bookings.addBookingDialog.form.note")}`}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            error={!!errors.note}
                                            helperText={
                                                errors.note?.message ? t(errors.note.message) : ""
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </FormProvider>
        </BasicDialog>
    );
};
